import { useEffect, useMemo, useRef, useState } from 'react';
import personApi from '~/apis/person';
import useDebounce from '~/hooks/useDebounce';
import { handleApiError } from '~/utils/helper';
import type { IPerson } from '~/utils/interface/person';
import {
  SUBJECT_MENU_DISPLAY_LIMIT,
  buildReportPersonListParams,
  filterPersonsWithLabel,
  personMenuLabel,
  personRowId,
} from './personSubjectFilter';

const REPORT_SUBJECT_SEARCH_DEBOUNCE_MS = 300;

function mergePersonsIntoMap(prev: Map<string, IPerson>, list: IPerson[]): Map<string, IPerson> {
  let changed = false;
  const next = new Map(prev);
  for (const p of list) {
    const id = personRowId(p);
    if (!id || next.has(id)) continue;
    next.set(id, p);
    changed = true;
  }
  return changed ? next : prev;
}

export function useReportSubjectPersons(username: string | undefined, subjectSearchInput: string) {
  const [initialPersons, setInitialPersons] = useState<IPerson[]>([]);
  const [initialPersonIds, setInitialPersonIds] = useState<string[]>([]);
  const [listReady, setListReady] = useState(false);
  const [personById, setPersonById] = useState<Map<string, IPerson>>(() => new Map());
  const [searchPersons, setSearchPersons] = useState<IPerson[]>([]);

  const searchAbortRef = useRef<AbortController | null>(null);
  const lastSearchKeyRef = useRef<string>('');

  const immediateQuery = subjectSearchInput.trim();
  const debouncedQuery = useDebounce(immediateQuery, REPORT_SUBJECT_SEARCH_DEBOUNCE_MS);
  const isSearching = immediateQuery.length > 0;
  const debouncedSearchActive = debouncedQuery.length > 0;

  useEffect(() => {
    if (!username) return;

    let cancelled = false;
    setListReady(false);

    (async () => {
      try {
        const params = buildReportPersonListParams(username, {
          pageSize: SUBJECT_MENU_DISPLAY_LIMIT,
        });
        const res = await personApi.getList(params);
        if (cancelled) return;

        const data = res.data.result?.data ?? [];
        const ids = data
          .map((p) => personRowId(p))
          .filter(Boolean)
          .sort();

        setInitialPersons(data);
        setInitialPersonIds(ids);
        setPersonById(() => {
          const map = new Map<string, IPerson>();
          for (const p of data) {
            const id = personRowId(p);
            if (id) map.set(id, p);
          }
          return map;
        });
        setListReady(true);
      } catch (e) {
        if (!cancelled) {
          setInitialPersons([]);
          setInitialPersonIds([]);
          setPersonById(new Map());
          setListReady(true);
          handleApiError(e);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [username]);

  useEffect(() => {
    if (!username) {
      setInitialPersons([]);
      setInitialPersonIds([]);
      setPersonById(new Map());
      setListReady(false);
    }
  }, [username]);

  useEffect(() => {
    if (!username || !debouncedSearchActive) {
      searchAbortRef.current?.abort();
      setSearchPersons([]);
      lastSearchKeyRef.current = '';
      return;
    }

    const nextKey = `${username}|${debouncedQuery}`;
    if (nextKey === lastSearchKeyRef.current) return;
    lastSearchKeyRef.current = nextKey;

    searchAbortRef.current?.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;

    (async () => {
      try {
        const params = buildReportPersonListParams(username, {
          search: debouncedQuery,
          pageSize: SUBJECT_MENU_DISPLAY_LIMIT,
        });
        const res = await personApi.getList(params, { signal: controller.signal });

        const data = res.data.result?.data ?? [];
        const withLabel = filterPersonsWithLabel(data);

        setSearchPersons(withLabel);
        setPersonById((prev) => mergePersonsIntoMap(prev, withLabel));
      } catch (e: unknown) {
        if (controller.signal.aborted) return;
        setSearchPersons([]);
        handleApiError(e);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [username, debouncedQuery, debouncedSearchActive]);

  const subjectPersonsDisplayed = useMemo(() => {
    if (isSearching) {
      return searchPersons;
    }
    return filterPersonsWithLabel(initialPersons);
  }, [initialPersons, isSearching, searchPersons]);

  const resolvePersonLabel = useMemo(
    () => (id: string) => {
      const p = personById.get(id);
      if (!p) return id;
      const label = personMenuLabel(p);
      return label || id;
    },
    [personById]
  );

  return {
    subjectPersonsDisplayed,
    resolvePersonLabel,
    listReady,
    personsCount: initialPersonIds.length,
  };
}
